import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Home, MessageCircle, Heart, Clock, ChevronRight } from 'lucide-react';
import { productData, testimonials, sampleQuestions, cartService, whoIsThisFor, howItWorks } from '../mock';
import { toast } from 'sonner';

const BaithoHomePage = () => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [flippedCard, setFlippedCard] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Show sticky bar when user scrolls down
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePreOrder = () => {
    cartService.addToCart(quantity);
    navigate('/checkout');
  };

  const handleJoinWaitlist = () => {
    // Scroll to top first for immediate visual feedback
    window.scrollTo({ top: 0, behavior: 'auto' });
    // Then navigate to checkout
    navigate('/checkout');
  };

  const handleAddToCart = () => {
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative linen-texture paper-texture overflow-hidden">
        <div className="container mx-auto px-4 py-8">
          {/* Logo */}
          <div className="mb-8">
            <h1 className="font-serif text-4xl md:text-5xl font-bold" style={{ color: '#8a8635' }}>
              Baitho
            </h1>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium">
                    Join the Waitlist for Early Access ✨
                  </div>
                  <div className="inline-block bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-bold">
                    Only 100 Decks Available
                  </div>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-gray-800 leading-snug">
                  The questions your family has been waiting for you to ask
                </h2>
              </div>
              <p className="text-xl text-gray-700 leading-relaxed font-normal">
                150+ conversation starters for Indian families. Discover each other beyond your everyday roles with fun, laughter, and meaningful moments.
              </p>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-4xl font-bold text-gray-900">₹{productData.price}</span>
                <span className="text-2xl text-gray-400 line-through">₹{productData.originalPrice}</span>
                <Badge className="bg-green-100 text-green-800 border-0">Early Access Price</Badge>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-mustard hover:bg-[#D4A43D] text-gray-900 font-semibold shadow-lg text-lg px-10 py-7"
                  onClick={handleJoinWaitlist}
                >
                  Join the Waitlist
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-terracotta text-terracotta hover:bg-[#C47B64]/10 text-lg px-10 py-7"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                >
                  How It Works
                </Button>
              </div>
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Get early access!</span> Register now, receive confirmation within a few days, and get your deck shipped in <span className="font-semibold text-terracotta">December 2025 — just in time for the holidays.</span>
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={productData.images.hero} 
                  alt="Family in conversation around table" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Because everyone's more than just their role
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                Baitho helps you discover the person behind the parent, the dreamer behind the sibling, the storyteller behind the grandparent. Fun questions that spark real connections.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto flex items-center justify-center">
                  <Home className="w-16 h-16 text-terracotta" strokeWidth={1.5} />
                </div>
                <h4 className="font-serif text-xl font-semibold text-gray-900">Home-Grown</h4>
                <p className="text-gray-600">
                  Made for Indian families, by an Indian family
                </p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto flex items-center justify-center">
                  <MessageCircle className="w-16 h-16 text-terracotta" strokeWidth={1.5} />
                </div>
                <h4 className="font-serif text-xl font-semibold text-gray-900">Real Talk</h4>
                <p className="text-gray-600">
                  Questions that go beyond "How was your day?"
                </p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto flex items-center justify-center">
                  <Heart className="w-16 h-16 text-terracotta" strokeWidth={1.5} />
                </div>
                <h4 className="font-serif text-xl font-semibold text-gray-900">Connection</h4>
                <p className="text-gray-600">
                  Discover stories you never knew existed
                </p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto flex items-center justify-center">
                  <Clock className="w-16 h-16 text-terracotta" strokeWidth={1.5} />
                </div>
                <h4 className="font-serif text-xl font-semibold text-gray-900">Timeless</h4>
                <p className="text-gray-600">
                  Works at any age, any stage of family life
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Box & What's Included Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-terracotta/20 to-mustard/20 rounded-3xl blur-2xl"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={productData.images.productBox}
                    alt="Baitho conversation cards box and deck" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <Badge className="bg-terracotta/10 text-terracotta border-0 mb-4">
                    Sneak Peek
                  </Badge>
                  <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    What's Inside the Box?
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Every Baitho box is thoughtfully designed to spark conversations that matter.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-terracotta/10 rounded-full p-3 flex-shrink-0">
                      <span className="text-2xl">🎴</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg mb-1">150+ Conversation Cards</h4>
                      <p className="text-gray-600">
                        Thoughtfully crafted questions across 6 categories: Traditions, Memories, Generations, Belonging, Pride, and Lessons
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-terracotta/10 rounded-full p-3 flex-shrink-0">
                      <span className="text-2xl">📦</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg mb-1">Premium Box</h4>
                      <p className="text-gray-600">
                        Beautifully designed, travel-friendly box that looks great on your coffee table
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-terracotta/10 rounded-full p-3 flex-shrink-0">
                      <span className="text-2xl">✨</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg mb-1">Quality Card Stock</h4>
                      <p className="text-gray-600">
                        Premium matte finish cards that feel as good as they look
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-terracotta/10 rounded-full p-3 flex-shrink-0">
                      <span className="text-2xl">📖</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg mb-1">Instruction Card</h4>
                      <p className="text-gray-600">
                        Simple guide on how to use Baitho to create meaningful moments
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-beige">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                How It Works
              </h2>
              <p className="text-xl text-gray-700">
                Four simple steps to deeper family bonds
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <div key={step.id} className="text-center space-y-4">
                  <div className="relative">
                    <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-lg flex items-center justify-center mb-4">
                      <span className="text-5xl">{step.icon}</span>
                    </div>
                    {index < howItWorks.length - 1 && (
                      <ChevronRight className="hidden lg:block absolute top-10 -right-6 w-8 h-8 text-terracotta" />
                    )}
                  </div>
                  <h4 className="font-serif text-2xl font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Who needs Baitho? (Spoiler: Everyone)
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Whether you're 8 or 80, Baitho works. Because everyone has a story worth sharing.
              </p>
            </div>
            
            <div className="space-y-6">
              {/* First Row - 2 cards */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-orange-200 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-5xl">{whoIsThisFor[0].icon}</div>
                    <h4 className="font-serif text-2xl font-bold text-gray-900">
                      {whoIsThisFor[0].title}
                    </h4>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {whoIsThisFor[0].description}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 border-2 border-yellow-200 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-5xl">{whoIsThisFor[1].icon}</div>
                    <h4 className="font-serif text-2xl font-bold text-gray-900">
                      {whoIsThisFor[1].title}
                    </h4>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {whoIsThisFor[1].description}
                  </p>
                </div>
              </div>
              
              {/* Second Row - 3 cards */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-3">{whoIsThisFor[2].icon}</div>
                    <h4 className="font-serif text-2xl font-bold text-gray-900">
                      {whoIsThisFor[2].title}
                    </h4>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed text-center">
                    {whoIsThisFor[2].description}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-3">{whoIsThisFor[3].icon}</div>
                    <h4 className="font-serif text-2xl font-bold text-gray-900">
                      {whoIsThisFor[3].title}
                    </h4>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed text-center">
                    {whoIsThisFor[3].description}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-8 border-2 border-amber-200 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-3">{whoIsThisFor[4].icon}</div>
                    <h4 className="font-serif text-2xl font-bold text-gray-900">
                      {whoIsThisFor[4].title}
                    </h4>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed text-center">
                    {whoIsThisFor[4].description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Real families. Real stories. Real fun.
              </h2>
              <p className="text-xl text-gray-700">
                Don't just take our word for it
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="border-0 shadow-xl bg-white card-hover">
                  <CardContent className="p-8 space-y-6">
                    <p className="text-lg text-gray-700 leading-relaxed italic">
                      "{testimonial.text}"
                    </p>
                    <div className="pt-6 border-t-2 border-gray-100">
                      <p className="font-semibold text-gray-900 text-lg">{testimonial.name}</p>
                      <p className="text-gray-600">{testimonial.role}</p>
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-terracotta text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-mustard rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-block bg-red-500/80 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
            ⚡ Limited to 100 Decks Only
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-8">
            Your family's next favorite ritual starts here
          </h2>
          <p className="text-2xl mb-4 max-w-3xl mx-auto font-normal opacity-90">
            150+ questions. Endless memories. Join the waitlist for early access.
          </p>
          <div className="flex items-center justify-center gap-3 mb-12">
            <span className="text-3xl font-bold">₹{productData.price}</span>
            <span className="text-xl text-white/60 line-through">₹{productData.originalPrice}</span>
            <Badge className="bg-white/20 text-white border-0">Early Access</Badge>
          </div>
          <Button 
            size="lg" 
            className="bg-mustard hover:bg-[#D4A43D] text-gray-900 font-bold text-xl px-12 py-8 shadow-2xl"
            onClick={handleJoinWaitlist}
          >
            Join the Waitlist
          </Button>
          <p className="text-white/80 mt-8 text-lg">
            Register now • Confirmation in a few days • Shipping in December 2025 for the holidays
          </p>
        </div>
      </section>

      {/* Sticky Bottom Bar */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl transition-transform duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Product Info - Left Side */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif text-base sm:text-lg font-bold text-gray-900 truncate">Baitho</h3>
                <Badge className="bg-red-100 text-red-800 border-0 text-xs whitespace-nowrap">Only 100 Left</Badge>
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-base sm:text-lg font-bold text-gray-900">₹{productData.price}</span>
                <span className="text-xs sm:text-sm text-gray-400 line-through">₹{productData.originalPrice}</span>
              </div>
            </div>
            
            {/* CTA Button - Right Side */}
            <Button 
              size="default"
              className="bg-baitho-orange hover:bg-[#b85520] text-white font-bold px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base whitespace-nowrap flex-shrink-0"
              onClick={handleJoinWaitlist}
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaithoHomePage;
